/**
 * Global coordinator to prevent multiple simultaneous profile creation attempts
 * This ensures only one profile creation happens at a time across all components
 */

import { logger } from './logger';

// Synchronous flag to prevent multiple simultaneous calls
const profileCreationInProgress = new Set<string>();
const profileCreationPromises = new Map<string, Promise<void>>();

export async function coordinateProfileCreation(
  userId: string,
  createFn: () => Promise<void>
): Promise<void> {
  // Synchronous check - prevents multiple simultaneous calls BEFORE async coordinator
  if (profileCreationInProgress.has(userId)) {
    const existingPromise = profileCreationPromises.get(userId);
    if (existingPromise) {
      logger.debug('Profile creation already in progress, waiting for existing promise...', { userId });
      return existingPromise;
    }
  }
  
  // Mark as in progress immediately (synchronous)
  profileCreationInProgress.add(userId);
  
  // Start new profile creation
  const promise = (async () => {
    try {
      await createFn();
    } finally {
      // Clean up after completion
      profileCreationInProgress.delete(userId);
      profileCreationPromises.delete(userId);
    }
  })();
  
  // Store promise for other callers to wait on
  profileCreationPromises.set(userId, promise);
  
  return promise;
}
